import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, X, Icon } from '../../ui/Icon';
import Spinner from '../../ui/Spinner';

export interface ShellFooter {
  onBack?: () => void;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  nextLoading?: boolean;
}

interface Props {
  current: number;
  total: number;
  eyebrow: string;
  title: string;
  subtitle?: string;
  /** What's been decided so far, as short chips («Lisboa», «3–10 ago»…). */
  recap: string[];
  footer: ShellFooter | null;
  onClose: () => void;
  children: ReactNode;
}

/**
 * One question per screen: a progress bar, a big serif question, the answer
 * area, and the trip taking shape in the footer. Full screen at phone width,
 * a roomy card on desktop.
 */
export default function WizardShell({ current, total, eyebrow, title, subtitle, recap, footer, onClose, children }: Props) {
  const { t } = useTranslation();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center sm:p-6 animate-fade-in">
      <div className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm" onClick={onClose} />
      <div
        role="dialog" aria-modal="true" aria-label={t('dashboard.modalTitle')}
        className="relative z-10 flex flex-col w-full sm:max-w-2xl h-full sm:h-[min(720px,90vh)] bg-white dark:bg-slate-900 sm:rounded-3xl shadow-2xl overflow-hidden animate-slide-up"
      >
        <header className="flex items-center gap-4 px-5 sm:px-8 pt-5 pb-3">
          <div
            className="flex-1 flex gap-1.5" role="progressbar"
            aria-valuemin={1} aria-valuemax={total} aria-valuenow={current}
            aria-valuetext={t('dashboard.newTripWizard.progress', { current, total })}
          >
            {Array.from({ length: total }, (_, i) => (
              <span
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
                  i < current ? 'bg-teal-500' : 'bg-slate-200 dark:bg-slate-700'
                }`}
              />
            ))}
          </div>
          <span className="text-[11px] font-semibold tabular-nums text-slate-400">{current}/{total}</span>
          <button type="button" onClick={onClose} className="btn-icon w-8 h-8 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200" aria-label={t('common.close')}>
            <Icon as={X} size={18} />
          </button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-8 pb-6">
          <div key={title} className="animate-fade-in pt-4 sm:pt-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-teal-600 dark:text-teal-400 mb-2">{eyebrow}</p>
            <h2
              className="font-serif font-medium text-slate-900 dark:text-slate-50 leading-[1.08] mb-2"
              style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', letterSpacing: '-0.025em' }}
            >
              {title}
            </h2>
            {subtitle && <p className="text-[14px] text-slate-500 dark:text-slate-400 leading-relaxed max-w-lg">{subtitle}</p>}
            <div className="mt-6">{children}</div>
          </div>
        </div>

        {footer && (
          <footer className="flex items-center gap-3 px-5 sm:px-8 py-4 border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900">
            <div className="flex-1 min-w-0 hidden sm:flex flex-wrap gap-1.5">
              {recap.map(item => (
                <span key={item} className="text-[12px] font-medium px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 truncate max-w-[14rem]">
                  {item}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              {footer.onBack && (
                <button type="button" onClick={footer.onBack} className="btn-secondary !px-3.5" aria-label={t('dashboard.newTripWizard.back')}>
                  <Icon as={ArrowLeft} size={16} />
                </button>
              )}
              {footer.onNext && (
                <button
                  type="button" onClick={footer.onNext}
                  disabled={footer.nextDisabled || footer.nextLoading}
                  className="btn-primary min-w-[8.5rem] justify-center"
                >
                  {footer.nextLoading ? <Spinner size="sm" /> : (
                    <>
                      {footer.nextLabel ?? t('dashboard.newTripWizard.next')}
                      <Icon as={ArrowRight} size={16} />
                    </>
                  )}
                </button>
              )}
            </div>
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
